/**
 * Effective Config Resolver (v1)
 *
 * Canonical inheritance: GLOBAL (workflow) → CYCLE → TRACK → SESSION.
 *
 * Cycles and tracks can inherit everything from the global workflow, including:
 * - Criteria (which attributes)
 * - Value ranges / measurement scales (e.g. Low–Medium–High, 1–5 Numeric, Fibonacci)
 * - Values (the actual scale options)
 * Different cycles, tracks, or sessions then choose which scale to use; no wizard required.
 *
 * This file: session mode defaults, facilitator defaults, participation defaults.
 * Value/range governance (attribute configs, scales, presets) is resolved elsewhere
 * but follows the same GLOBAL → CYCLE → TRACK → SESSION chain.
 *
 * V1 keys only. Persisted DB-backed state only; no local-only memory.
 */

// ============================================================================
// V1 config shape and keys
// ============================================================================

export type SessionTimingMode = 'live' | 'async';
export type SessionEntryMode = 'facilitator' | 'participants';

/**
 * Effective session config (v1). Keys supported in first pass.
 */
export interface EffectiveSessionConfigV1 {
  /** Session timing: live (synchronous) or async */
  timingMode: SessionTimingMode;
  /** Who enters data: facilitator for all, or participants enter their own */
  entryMode: SessionEntryMode;
  /** Default facilitator user id when creating a session (optional) */
  defaultFacilitatorId: string | undefined;
}

/**
 * Partial layer for inheritance. Each scope can override any subset.
 */
export type EffectiveSessionConfigLayer = Partial<EffectiveSessionConfigV1>;

// ============================================================================
// Where each key is read from (v1)
// ============================================================================
//
// - timingMode:  global = DEFAULT_GLOBAL_SESSION_CONFIG; cycle = cycle.metadata.sessionDefaults; track = track.metadata.sessionDefaults; session = create params or last session
// - entryMode:   same
// - defaultFacilitatorId: global = undefined; cycle/track = metadata.sessionDefaults.defaultFacilitatorId; session = create params
//
// Fallback: cycle and track layers use metadata.sessionDefaults until dedicated DB columns exist (e.g. cycle.default_session_timing_mode).
// When reading, we accept { timingMode?, entryMode?, defaultFacilitatorId? } in metadata.sessionDefaults.
//

/** Shape of session defaults stored in cycle.metadata.sessionDefaults or track.metadata.sessionDefaults (v1 fallback) */
export interface SessionDefaultsInMetadata {
  timingMode?: SessionTimingMode;
  entryMode?: SessionEntryMode;
  defaultFacilitatorId?: string;
  /** Default result resolution for tracks in this cycle (e.g. latest_published, aggregate). */
  resultResolution?: string;
  /** Default session duration in minutes. */
  defaultDurationMinutes?: number;
  /** Override catchball enabled for this cycle (when workflow allows). */
  catchballEnabled?: boolean;
}

/** Global defaults (v1: code default; later: workspace/DB) */
export const DEFAULT_GLOBAL_SESSION_CONFIG: EffectiveSessionConfigV1 = {
  timingMode: 'live',
  entryMode: 'facilitator',
  defaultFacilitatorId: undefined,
};

// ============================================================================
// Resolver
// ============================================================================

/**
 * Resolve effective config from inheritance layers.
 * Later scope overrides earlier; undefined means inherit.
 *
 * Order: workspace (global) → cycle → track → session.
 * Base = DEFAULT_GLOBAL_SESSION_CONFIG; workspace layer overlays on that (null/unset = use system default).
 */
export function resolveEffectiveConfig(
  global: EffectiveSessionConfigLayer,
  cycle?: EffectiveSessionConfigLayer | null,
  track?: EffectiveSessionConfigLayer | null,
  session?: EffectiveSessionConfigLayer | null
): EffectiveSessionConfigV1 {
  const g = { ...DEFAULT_GLOBAL_SESSION_CONFIG, ...global };
  return {
    timingMode: session?.timingMode ?? track?.timingMode ?? cycle?.timingMode ?? g.timingMode,
    entryMode: session?.entryMode ?? track?.entryMode ?? cycle?.entryMode ?? g.entryMode,
    defaultFacilitatorId: session?.defaultFacilitatorId ?? track?.defaultFacilitatorId ?? cycle?.defaultFacilitatorId ?? g.defaultFacilitatorId,
  };
}

/** Workspace-like object for session-defaults resolution (global layer). */
export type WorkspaceSessionDefaultsInput = {
  defaultTimingMode?: string | null;
  defaultEntryMode?: string | null;
  defaultFacilitatorId?: string | null;
  default_timing_mode?: string | null;
  default_entry_mode?: string | null;
  default_facilitator_id?: string | null;
} | null | undefined;

/**
 * Build workspace (global) layer from persisted workspace.
 * Reads first-class columns default_timing_mode, default_entry_mode, default_facilitator_id
 * (or camelCase defaultTimingMode, defaultEntryMode, defaultFacilitatorId from client).
 * Returns null if workspace has no overrides.
 */
export function workspaceLayerFromWorkspace(workspace: WorkspaceSessionDefaultsInput): EffectiveSessionConfigLayer | null {
  if (!workspace || typeof workspace !== 'object') return null;
  const layer: EffectiveSessionConfigLayer = {};
  const w = workspace as Record<string, unknown>;
  const timing = (w.defaultTimingMode ?? w.default_timing_mode) as string | undefined;
  const entry = (w.defaultEntryMode ?? w.default_entry_mode) as string | undefined;
  const facilitator = (w.defaultFacilitatorId ?? w.default_facilitator_id) as string | undefined;
  if (timing === 'live' || timing === 'async') layer.timingMode = timing;
  if (entry === 'facilitator' || entry === 'participants') layer.entryMode = entry;
  if (facilitator != null && String(facilitator).trim()) layer.defaultFacilitatorId = String(facilitator).trim();
  return Object.keys(layer).length ? layer : null;
}

/**
 * Build a session-layer override from a prior session's modeConfig and facilitator.
 * Used to carry forward last session's choices as the session override.
 */
export function sessionLayerFromPriorSession(prior: {
  modeConfig?: { timingMode?: SessionTimingMode; entryMode?: SessionEntryMode };
  facilitatorId?: string;
}): EffectiveSessionConfigLayer {
  return {
    timingMode: prior.modeConfig?.timingMode,
    entryMode: prior.modeConfig?.entryMode,
    defaultFacilitatorId: prior.facilitatorId,
  };
}

/**
 * Build cycle layer from persisted cycle.
 * Prefers first-class columns (defaultTimingMode, defaultEntryMode, defaultFacilitatorId) when present;
 * falls back to cycle.metadata.sessionDefaults.
 */
export function cycleLayerFromCycle(cycle: {
  metadata?: Record<string, unknown> | null;
  defaultTimingMode?: SessionTimingMode;
  defaultEntryMode?: SessionEntryMode;
  defaultFacilitatorId?: string;
} | null | undefined): EffectiveSessionConfigLayer | null {
  if (!cycle) return null;
  const layer: EffectiveSessionConfigLayer = {};
  // First-class columns (from degap_cycles.default_timing_mode etc.)
  if (cycle.defaultTimingMode === 'live' || cycle.defaultTimingMode === 'async') layer.timingMode = cycle.defaultTimingMode;
  if (cycle.defaultEntryMode === 'facilitator' || cycle.defaultEntryMode === 'participants') layer.entryMode = cycle.defaultEntryMode;
  if (cycle.defaultFacilitatorId != null && String(cycle.defaultFacilitatorId).trim()) layer.defaultFacilitatorId = String(cycle.defaultFacilitatorId).trim();
  if (Object.keys(layer).length > 0) return layer;
  // Fallback: metadata.sessionDefaults
  if (!cycle.metadata || typeof cycle.metadata !== 'object') return null;
  const d = cycle.metadata.sessionDefaults;
  if (!d || typeof d !== 'object') return null;
  const defs = d as SessionDefaultsInMetadata;
  if (defs.timingMode === 'live' || defs.timingMode === 'async') layer.timingMode = defs.timingMode;
  if (defs.entryMode === 'facilitator' || defs.entryMode === 'participants') layer.entryMode = defs.entryMode;
  if (defs.defaultFacilitatorId != null && String(defs.defaultFacilitatorId).trim()) layer.defaultFacilitatorId = String(defs.defaultFacilitatorId).trim();
  return Object.keys(layer).length ? layer : null;
}

/**
 * Build track layer from persisted track.
 * Prefers first-class columns (defaultTimingMode, defaultEntryMode, defaultFacilitatorId) when present;
 * falls back to track.metadata.sessionDefaults.
 */
export function trackLayerFromTrack(track: {
  metadata?: Record<string, unknown> | null;
  defaultTimingMode?: SessionTimingMode | null;
  defaultEntryMode?: SessionEntryMode | null;
  defaultFacilitatorId?: string | null;
} | null | undefined): EffectiveSessionConfigLayer | null {
  if (!track) return null;
  const layer: EffectiveSessionConfigLayer = {};
  // First-class columns (from tracks.default_timing_mode etc.)
  if (track.defaultTimingMode === 'live' || track.defaultTimingMode === 'async') layer.timingMode = track.defaultTimingMode;
  if (track.defaultEntryMode === 'facilitator' || track.defaultEntryMode === 'participants') layer.entryMode = track.defaultEntryMode;
  if (track.defaultFacilitatorId != null && String(track.defaultFacilitatorId).trim()) layer.defaultFacilitatorId = String(track.defaultFacilitatorId).trim();
  if (Object.keys(layer).length > 0) return layer;
  // Fallback: metadata.sessionDefaults
  if (!track.metadata || typeof track.metadata !== 'object') return null;
  const d = track.metadata.sessionDefaults;
  if (!d || typeof d !== 'object') return null;
  const defs = d as SessionDefaultsInMetadata;
  if (defs.timingMode === 'live' || defs.timingMode === 'async') layer.timingMode = defs.timingMode;
  if (defs.entryMode === 'facilitator' || defs.entryMode === 'participants') layer.entryMode = defs.entryMode;
  if (defs.defaultFacilitatorId != null && String(defs.defaultFacilitatorId).trim()) layer.defaultFacilitatorId = String(defs.defaultFacilitatorId).trim();
  return Object.keys(layer).length ? layer : null;
}

// ============================================================================
// Inheritance breakdown for UI (what value comes from which layer)
// ============================================================================

export type SessionDefaultsScope = 'global' | 'cycle' | 'track' | 'session';

/** Per-key breakdown: value at each layer and which layer is the source of the effective value */
export interface SessionDefaultsKeyBreakdown {
  /** Value at global (workspace or system default) */
  global: string;
  /** What the "global" box is really: the level above the cycle the server read (S-40). */
  globalLabel?: string;
  /** Value at cycle (or "—" if not set) */
  cycle: string;
  /** Value at track (or "—" if not set) */
  track: string;
  /** Value at session (or "—" if not set) */
  session: string;
  /** Effective value after inheritance */
  effective: string;
  /** Which layer provided the effective value */
  source: SessionDefaultsScope;
}

export interface SessionDefaultsInheritanceBreakdown {
  timingMode: SessionDefaultsKeyBreakdown;
  entryMode: SessionDefaultsKeyBreakdown;
  defaultFacilitatorId: SessionDefaultsKeyBreakdown;
  /** Resolved effective config (for convenience) */
  effective: EffectiveSessionConfigV1;
}

function formatLayerValue<T>(v: T | undefined | null, formatter: (x: T) => string): string {
  if (v === undefined || v === null || (typeof v === 'string' && v.trim() === '')) return '—';
  return formatter(v as T);
}

/**
 * Build inheritance breakdown for UI: for each key, show value at global, cycle, track, session,
 * the effective value, and which layer it came from. Use for "what is being inherited" displays.
 */
export function getSessionDefaultsInheritanceBreakdown(
  globalLayer: EffectiveSessionConfigLayer | null,
  cycleLayer: EffectiveSessionConfigLayer | null,
  trackLayer: EffectiveSessionConfigLayer | null,
  sessionLayer: EffectiveSessionConfigLayer | null,
  options?: { facilitatorLabel?: (id: string) => string }
): SessionDefaultsInheritanceBreakdown {
  const g = { ...DEFAULT_GLOBAL_SESSION_CONFIG, ...globalLayer };
  const facLabel = options?.facilitatorLabel ?? ((id: string) => id);

  const effective = resolveEffectiveConfig(
    globalLayer ?? DEFAULT_GLOBAL_SESSION_CONFIG,
    cycleLayer ?? undefined,
    trackLayer ?? undefined,
    sessionLayer ?? undefined
  );

  const sourceOf = (
    key: keyof EffectiveSessionConfigV1
  ): SessionDefaultsScope => {
    if (sessionLayer?.[key] !== undefined && sessionLayer?.[key] !== null && String(sessionLayer[key]).trim() !== '') return 'session';
    if (trackLayer?.[key] !== undefined && trackLayer?.[key] !== null && String(trackLayer[key]).trim() !== '') return 'track';
    if (cycleLayer?.[key] !== undefined && cycleLayer?.[key] !== null && String(cycleLayer[key]).trim() !== '') return 'cycle';
    return 'global';
  };

  return {
    timingMode: {
      global: formatLayerValue(g.timingMode, (x) => x),
      cycle: formatLayerValue(cycleLayer?.timingMode, (x) => x),
      track: formatLayerValue(trackLayer?.timingMode, (x) => x),
      session: formatLayerValue(sessionLayer?.timingMode, (x) => x),
      effective: effective.timingMode,
      source: sourceOf('timingMode'),
    },
    entryMode: {
      global: formatLayerValue(g.entryMode, (x) => x),
      cycle: formatLayerValue(cycleLayer?.entryMode, (x) => x),
      track: formatLayerValue(trackLayer?.entryMode, (x) => x),
      session: formatLayerValue(sessionLayer?.entryMode, (x) => x),
      effective: effective.entryMode,
      source: sourceOf('entryMode'),
    },
    defaultFacilitatorId: {
      global: formatLayerValue(g.defaultFacilitatorId, (id) => facLabel(id)),
      cycle: formatLayerValue(cycleLayer?.defaultFacilitatorId, (id) => facLabel(id)),
      track: formatLayerValue(trackLayer?.defaultFacilitatorId, (id) => facLabel(id)),
      session: formatLayerValue(sessionLayer?.defaultFacilitatorId, (id) => facLabel(id)),
      effective: effective.defaultFacilitatorId ? facLabel(effective.defaultFacilitatorId) : '—',
      source: sourceOf('defaultFacilitatorId'),
    },
    effective,
  };
}

/**
 * Resolve effective session config and inheritance breakdown for a session.
 * Convenience helper for SetupTab. Accepts optional
 * cycle, track, workspace (for isolated sessions).
 *
 * @param session - Session with modeConfig and facilitatorId (session-layer override)
 * @param cycle - Cycle if session is cycle-linked
 * @param track - Track if session is track-linked
 * @param workspace - Workspace for global layer (or null for system defaults)
 */
export function getEffectiveForSession(
  session: { modeConfig?: { timingMode?: SessionTimingMode; entryMode?: SessionEntryMode }; facilitatorId?: string },
  cycle?: Parameters<typeof cycleLayerFromCycle>[0],
  track?: Parameters<typeof trackLayerFromTrack>[0],
  workspace?: WorkspaceSessionDefaultsInput,
  options?: { facilitatorLabel?: (id: string) => string }
): SessionDefaultsInheritanceBreakdown {
  const workspaceLayer = workspaceLayerFromWorkspace(workspace);
  const cycleLayer = cycle ? cycleLayerFromCycle(cycle) : null;
  const trackLayer = track ? trackLayerFromTrack(track) : null;
  const sessionLayer = sessionLayerFromPriorSession({
    modeConfig: session.modeConfig,
    facilitatorId: session.facilitatorId,
  });
  return getSessionDefaultsInheritanceBreakdown(
    workspaceLayer ?? DEFAULT_GLOBAL_SESSION_CONFIG,
    cycleLayer,
    trackLayer,
    sessionLayer,
    options
  );
}

// ============================================================================
// Session settings on the chain: session → track → cycle → workflow → workspace
// (S-33 "Change ranking order", migration 891; the room's timer rule and
// anonymity, S-2 and S-3, migration 973)
// ============================================================================

/** A level a session setting can be set at. */
export type SessionPolicyLevel = 'session' | 'track' | 'cycle' | 'workflow' | 'workspace';
/** Where a setting was decided; 'default' when no level sets it. */
export type SessionPolicySource = SessionPolicyLevel | 'default';
/** A setting's value at each level (null/undefined = inherit). */
export type SessionPolicyLayers<T> = Partial<Record<SessionPolicyLevel, T | null | undefined>>;

// reuse-ack: mirrors internal_session_policy_value (supabase/migrations/973_the_host_runs_the_room.sql) — settings screens show the inherited value at levels where no session exists yet; the server copy is the one that decides.
/** Most specific level wins: session → track → cycle → workflow → workspace, else the default. */
export function resolveSessionPolicy<T>(
  layers: SessionPolicyLayers<T>,
  fallback: T,
): { value: T; source: SessionPolicySource } {
  const order: SessionPolicyLevel[] = ['session', 'track', 'cycle', 'workflow', 'workspace'];
  for (const level of order) {
    const v = layers[level];
    if (v !== null && v !== undefined) return { value: v, source: level };
  }
  return { value: fallback, source: 'default' };
}

/** Where the "Change ranking order" switch was decided. */
export type RankOrderChangeSource = SessionPolicySource;

/** The switch's value at each level (null/undefined = inherit). */
export type RankOrderChangeLayers = SessionPolicyLayers<boolean>;

/**
 * "Change ranking order" (S-33): with nothing set the answer is yes (owner
 * ruling 2026-10-14: today's behavior).
 */
export function resolveRankOrderChange(layers: RankOrderChangeLayers): { allowed: boolean; source: RankOrderChangeSource } {
  const r = resolveSessionPolicy(layers, true);
  return { allowed: r.value, source: r.source };
}
